import React from 'react';
import {twMerge} from 'tailwind-merge';

import {AuthState, useUserContext} from 'Contexts/UserContext/UserContext';
import {Loading} from './Loading';
import {NavBar} from './NavBar';
import {Link, useLocation} from 'react-router-dom';
import {CodeSimple, House, User} from '@phosphor-icons/react';
import colors from 'tailwindcss/colors';

interface Props {
    className?: string;
    showNavBar?: boolean;
    ignoreLoading?: boolean;
    dark?: boolean;
}

interface RouteButtonProps {
    to: string;
    text: string;
    icon: (color) => React.ReactNode;
    dark?: boolean;
}

export const RouteButton: React.FC<RouteButtonProps> = ({to, text, icon, dark}) => {
    const location = useLocation();

    const isActive = location.pathname === to;

    let iconColor = '#808080';

    if (dark && isActive) {
        iconColor = 'white';
    } else if (dark) {
        iconColor = colors.neutral['400'];
    } else if (isActive) {
        iconColor = 'black';
    }

    return (
        <Link
            aria-disabled={isActive}
            className={twMerge(
                'flex h-12 w-full flex-row items-center justify-start gap-2.5 rounded-lg p-2.5 text-[#808080]',
                isActive && 'bg-[#F4F4F4] text-black',
                dark && 'text-neutral-400',
                dark && isActive && 'bg-neutral-500/30 text-white'
            )}
            to={to}
        >
            {icon(iconColor)}
            <p className="whitespace-nowrap text-[12px] font-bold">{text}</p>
        </Link>
    );
};

export const PageContainer: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    className,
    dark,
    showNavBar = true,
    ignoreLoading = false,
}) => {
    const {status} = useUserContext();
    const isLoading = status === AuthState.USER_LOADING;
    const unpaid = status !== AuthState.SIGNED_IN_PAID;
    return (
        <div
            className={twMerge(
                'flex h-screen max-h-screen w-full flex-col items-center justify-start bg-white',
                dark && 'bg-neutral-800',
                className
            )}
        >
            {showNavBar && <NavBar dark={dark} />}
            <div className="height-minus-nav flex w-full flex-row gap-2.5 p-2.5">
                <div className="flex h-full w-52 flex-col">
                    <RouteButton dark={dark} to="/" text="Home" icon={(color) => <House size={24} color={color} />} />
                    {!unpaid && (
                        <>
                            <RouteButton
                                dark={dark}
                                to="/tailwindconfig"
                                text="Code Config"
                                icon={(color) => <CodeSimple size={24} color={color} />}
                            />
                            <RouteButton
                                dark={dark}
                                to="/manage"
                                text="Account"
                                icon={(color) => <User size={24} color={color} />}
                            />
                        </>
                    )}
                </div>
                <div className="flex h-full w-full overflow-y-auto rounded-[10px] border border-[#ECECEC]">
                    {isLoading && !ignoreLoading ? <Loading /> : children}
                </div>
            </div>
        </div>
    );
};
